import React, { useEffect, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import TextField from "aldoo-ra/TextField"
import Typography from "aldoo-ra/Typography"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Tabs from "aldoo-ra/Tabs"
import { createProject, updateProject, setProjectPublic } from "../api"
import ComponentList from "../Components/ComponentList"
import MemberManager from "../Members/MemberManager"

function makeSlug(text) {
  return String(text || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
}

const ProjectEditor = ({ orgId, project, onClose }) => {
  const isNew = !project?._id
  const [name, setName] = useState(project?.name || "")
  const [slug, setSlug] = useState(project?.slug || "")
  const [description, setDescription] = useState(project?.description || "")
  const slugTouched = useRef(!!project?.slug)

  useEffect(() => {
    if (!slugTouched.current && name) {
      setSlug(makeSlug(name))
    }
  }, [name])

  const [isPublic, setIsPublic] = useState(!!project?.public)
  const [publicSlug, setPublicSlug] = useState(project?.publicSlug || "")
  const [saved, setSaved] = useState(project)
  const [err, setErr] = useState(null)
  const [tab, setTab] = useState("details")

  const save = async () => {
    setErr(null)
    const oid = typeof orgId === "object" ? (orgId?.value || orgId?.[0]?.value || String(orgId)) : orgId
    if (isNew) {
      const r = await createProject({ orgId: oid, name, slug, description })
      if (r?.error) return setErr(r.error)
      setSaved(r.project)
    } else {
      const r = await updateProject({ projectId: project._id, name, slug, description })
      if (r?.error) return setErr(r.error)
      setSaved(r.project)
    }
  }

  const togglePublic = async (v) => {
    setIsPublic(v)
    if (!saved?._id) return
    const r = await setProjectPublic({
      projectId: saved._id,
      public: v,
      publicSlug: v ? (publicSlug || slug) : undefined,
    })
    if (r?.error) setErr(r.error)
    else setSaved(r.project)
  }

  const tabs = saved?._id
    ? [
        { value: "details", label: "Details" },
        { value: "components", label: "Components" },
        { value: "members", label: "Members" },
        { value: "settings", label: "Settings" },
      ]
    : [{ value: "details", label: "Details" }]

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Typography variant="admin_h2">{isNew ? "New project" : `Edit: ${saved?.name || name}`}</Typography>
        <Button variant="secondary" onClick={onClose}>Back</Button>
      </div>

      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {tab === "details" && (
        <div className="space-y-3 max-w-2xl">
          <TextField label="Name" value={name} onChange={setName} />
          <TextField label="Slug" value={slug} onChange={(v) => { slugTouched.current = true; setSlug(v) }} />
          <TextField label="Description" value={description} onChange={setDescription} />
          <div className="flex gap-2">
            <Button onClick={save}>{isNew ? "Create" : "Save"}</Button>
            {err && <div className="text-red-500 text-sm self-center">Error: {err}</div>}
          </div>
        </div>
      )}

      {tab === "components" && saved?._id && <ComponentList projectId={saved._id} />}
      {tab === "members" && saved?._id && <MemberManager projectId={saved._id} canManage={saved._role === "owner" || !saved._role} />}
      {tab === "settings" && saved?._id && (
        <div className="max-w-2xl space-y-3">
          <Typography variant="admin_h3">Public status page</Typography>
          <div className="flex items-center gap-3">
            <ToggleSwitch checked={isPublic} onChange={togglePublic} />
            <TextField
              label="Public slug"
              value={publicSlug}
              onChange={setPublicSlug}
              disabled={!isPublic}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectEditor
